import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';
import { sortEdgesByOrder } from '@utils/sort';
import { normalizeUrl } from '@utils/url';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .filters {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 25px;
    justify-content: center;
  }

  .filter-btn {
    ${({ theme }) => theme.mixins.smallButton};
    padding: 10px 14px;
    background-color: transparent;
    color: var(--light-slate);

    &.active {
      color: var(--green);
      border-color: var(--green);
    }

    &:hover,
    &:focus {
      background-color: var(--lightest-navy);
      color: var(--green);
    }
  }

  .archive-button {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 25px;
  }

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 20px;
    width: 100%;
    max-width: 1100px;
    position: relative;
    margin-top: 50px;

    @media (max-width: 1080px) {
      grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    }
  }
`;

const StyledProject = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);
  height: 100%;

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .project-inner {
        transform: translateY(-7px);
      }
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    width: 100%;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    overflow: auto;
  }

  .project-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    position: relative;
    margin-bottom: 35px;

    .folder {
      color: var(--green);
      svg {
        width: 40px;
        height: 40px;
      }
    }

    .project-links {
      margin-left: auto;
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--light-slate);

      a,
      span {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-family: var(--font-mono);
        font-size: var(--fz-xxs);
        padding: 8px 12px;
        border-radius: 999px;
        border: 1px solid var(--lightest-navy);
        background-color: rgba(2, 12, 27, 0.25);
        white-space: nowrap;
      }

      a {
        color: var(--green);

        &:hover,
        &:focus {
          background-color: rgba(100, 255, 218, 0.08);
          border-color: var(--green);
        }
      }

      .secondary {
        color: var(--light-slate);
      }

      .status {
        color: var(--slate);
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  header {
    width: 100%;
  }

  footer {
    width: 100%;
  }

  .project-title {
    margin: 0 0 10px;
    color: var(--lightest-slate);
    font-size: var(--fz-xxl);

    a {
      position: static;

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  .project-description {
    color: var(--light-slate);
    font-size: 17px;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-tech-list {
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    flex-wrap: wrap;
    padding: 0;
    margin: 20px 0 0 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: 15px;
      }
    }
  }
`;

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/user/projects/" }
          frontmatter: { showInProjects: { ne: false }, showInGames: { ne: true } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              date
              order
              tech
              github
              external
              category
            }
            html
          }
        }
      }
    }
  `);

  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const GRID_LIMIT = 3;
  const allProjects = sortEdgesByOrder(
    data.projects.edges.filter(({ node }) => node),
    (a, b) => {
      const aDate = a?.node?.frontmatter?.date ? new Date(a.node.frontmatter.date).getTime() : null;
      const bDate = b?.node?.frontmatter?.date ? new Date(b.node.frontmatter.date).getTime() : null;
      const aMs = Number.isFinite(aDate) ? aDate : -Infinity;
      const bMs = Number.isFinite(bDate) ? bDate : -Infinity;
      if (aMs !== bMs) {return bMs - aMs;} // date DESC
      return String(a?.node?.frontmatter?.title || '').localeCompare(
        String(b?.node?.frontmatter?.title || ''),
      );
    },
  );
  if (!allProjects || allProjects.length === 0) {
    return null;
  }

  const categoryOptions = useMemo(() => {
    const categories = new Set();
    allProjects.forEach(({ node }) => {
      const raw = node.frontmatter?.category;
      if (!raw) {return;}
      String(raw)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .forEach(c => categories.add(c));
    });
    return ['All', ...Array.from(categories)];
  }, [allProjects]);

  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProjectsToShow = useMemo(() => {
    const filtered =
      activeCategory === 'All'
        ? allProjects
        : allProjects.filter(({ node }) => {
          const raw = node.frontmatter?.category;
          if (!raw) {return false;}
          return String(raw)
            .split(',')
            .map(s => s.trim())
            .includes(activeCategory);
        });

    return filtered.slice(0, GRID_LIMIT);
  }, [activeCategory, allProjects]);

  const projectInner = node => {
    const { frontmatter, html } = node;
    const { github, external, title, tech, category } = frontmatter;
    const safeHtml = typeof html === 'string' ? html.trim() : '';
    const externalUrl = normalizeUrl(external);
    const githubUrl = normalizeUrl(github);
    const titleHref = externalUrl || githubUrl || '';
    const descriptionHtml = safeHtml
      ? safeHtml
      : category
        ? `<p>Category: ${category}</p>`
        : '<p>More details coming soon.</p>';

    return (
      <div className="project-inner">
        <header>
          <div className="project-top">
            <div className="folder">
              <Icon name="Folder" />
            </div>
            <div className="project-links">
              {externalUrl && (
                <a href={externalUrl} aria-label="View Project" target="_blank" rel="noreferrer">
                  View project
                </a>
              )}

              {githubUrl && (
                <a
                  href={githubUrl}
                  aria-label="View Code"
                  className="secondary"
                  target="_blank"
                  rel="noreferrer">
                  View code <Icon name="GitHub" />
                </a>
              )}
            </div>
          </div>

          <h3 className="project-title">
            {titleHref ? (
              <a href={titleHref} target="_blank" rel="noreferrer">
                {title}
              </a>
            ) : (
              <span>{title}</span>
            )}
          </h3>

          <div
            className="project-description"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </header>

        <footer>
          {tech && (
            <ul className="project-tech-list">
              {tech.map((tech, i) => (
                <li key={i}>{tech}</li>
              ))}
            </ul>
          )}
        </footer>
      </div>
    );
  };

  return (
    <StyledProjectsSection id="other-projects">
      <h2 ref={revealTitle}>Other Noteworthy Projects</h2>

      {categoryOptions.length > 1 && (
        <ul className="filters" aria-label="Project filters">
          {categoryOptions.map(category => (
            <li key={category}>
              <button
                type="button"
                className={`filter-btn${activeCategory === category ? ' active' : ''}`}
                onClick={() => setActiveCategory(category)}>
                {category}
              </button>
            </li>
          ))}
        </ul>
      )}

      <ul className="projects-grid">
        {prefersReducedMotion ? (
          <>
            {filteredProjectsToShow &&
              filteredProjectsToShow.map(({ node }, i) => (
                <StyledProject key={i}>{projectInner(node)}</StyledProject>
              ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {filteredProjectsToShow &&
              filteredProjectsToShow.map(({ node }, i) => (
                <CSSTransition
                  key={i}
                  classNames="fadeup"
                  timeout={i >= GRID_LIMIT ? (i - GRID_LIMIT) * 300 : 300}
                  exit={false}>
                  <StyledProject
                    key={i}
                    ref={el => (revealProjects.current[i] = el)}
                    style={{
                      transitionDelay: `${i >= GRID_LIMIT ? (i - GRID_LIMIT) * 100 : 0}ms`,
                    }}>
                    {projectInner(node)}
                  </StyledProject>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </ul>

      <Link className="archive-button" to="/archive">
        view the archive
      </Link>
    </StyledProjectsSection>
  );
};

export default Projects;
